import { createFileRoute } from '@tanstack/react-router'
import { ButtonLink } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { PlusIcon } from 'lucide-react'
import { useLingui } from '@lingui/react/macro'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useModalLink } from '@/components/custom/modal-slot'
import { OrgOverviewItem } from './-components/orgs/org-overview-item'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint } from './-components/shared/collapsible'
import { SectionLabel } from '@/components/custom/section-label'
import { OrgsOverviewPending } from './-components/shared/list-pane-pending'

export const Route = createFileRoute('/_app/orgs/')({
  component: RouteComponent,
  pendingComponent: OrgsOverviewPending,
})

function RouteComponent() {
  const context = Route.useRouteContext()
  const { t } = useLingui()
  const [searchQuery, setSearchQuery] = useState('')
  const createOrgLink = useModalLink('/@modal/orgs/new')
  const { data: orgs, isFetching: orgsFetching } = useSuspenseQuery(context.orgsListQueryOptions)
  const orgsCount = orgs.length

  const orgQuery = searchQuery.toLowerCase()
  const filteredOrgs =
    searchQuery.trim() === '' ? orgs : orgs.filter((org) => org.name.toLowerCase().includes(orgQuery))

  return (
    <div className="flex h-full flex-col">
      <div className="rule-fade-left relative flex items-center gap-2 px-4 py-3">
        <SearchInput placeholder={t`Search administrations...`} value={searchQuery} onChange={setSearchQuery} />
        <ButtonLink variant="secondary" {...createOrgLink}>
          <PlusIcon className="size-4" />
          {t`Create administration`}
        </ButtonLink>
      </div>

      <ScrollableList
        isFetching={orgsFetching}
        isEmpty={filteredOrgs.length === 0}
        hint={orgsCount > 0 && <SelectionHint visible text={t`Select an administration to view its groups`} />}
        emptyState={
          searchQuery ? (
            <PanelEmptyState
              noResults
              icon={<NAVIGATION_ICONS.organizations className="text-muted-foreground size-5" />}
              title=""
              description=""
            />
          ) : (
            <PanelEmptyState
              icon={<NAVIGATION_ICONS.organizations className="text-muted-foreground size-5" />}
              title={t`No administrations yet`}
              description={t`Create your first administration to get started.`}
              action={
                <ButtonLink variant="secondary" {...createOrgLink}>
                  <PlusIcon className="size-4" />
                  {t`Create administration`}
                </ButtonLink>
              }
            />
          )
        }
      >
        {orgsCount > 0 && (
          <SectionLabel className="px-2">
            {orgsCount === 1 ? t`1 administration` : t`${orgsCount} administrations`}
          </SectionLabel>
        )}
        {filteredOrgs.map((org) => (
          <OrgOverviewItem key={org.id} org={org} />
        ))}
      </ScrollableList>
    </div>
  )
}
